// RecentLeads.jsx — Modern card-based recent leads display for dashboard
// Replaces the old table layout with responsive lead cards

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, ArrowRight, Plus, Flame, Snowflake, Thermometer } from "lucide-react";
import { motion } from "framer-motion";
import "../../css/dashboard/recentLeads.css";

const STATUS_LABEL = {
  new:        "New",
  contacted:  "Contacted",
  interested: "Interested",
  follow_up:  "Follow-up",
  closed:     "Closed",
  lost:       "Lost",
};

const STATUS_COLOR = {
  new:        { bg: "#EFF6FF", text: "#1D4ED8" },
  contacted:  { bg: "#F5F3FF", text: "#6D28D9" },
  interested: { bg: "#ECFDF5", text: "#065F46" },
  follow_up:  { bg: "#FFFBEB", text: "#92400E" },
  closed:     { bg: "#F0FDF4", text: "#15803D" },
  lost:       { bg: "#FEF2F2", text: "#991B1B" },
};

const INTEREST_CONFIG = {
  hot:  { icon: Flame,       color: "#EF4444", label: "Hot"  },
  warm: { icon: Thermometer, color: "#F59E0B", label: "Warm" },
  cold: { icon: Snowflake,   color: "#3B82F6", label: "Cold" },
};

const formatPhone = (p) => {
  if (!p) return "—";
  const digits = p.replace(/\D/g, "");
  if (digits.length === 10) return digits.replace(/(\d{5})(\d{5})/, "$1 $2");
  return p;
};

const RecentLeads = ({ leads = [], onAddLead }) => {
  const [sortBy, setSortBy] = useState("recent");
  const [filterStatus, setFilterStatus] = useState("all");

  // ── Filter to last 7 days ──────────────────────────────────────────────
  // Lead model uses custom timestamp: { createdAt: "createdDate", updatedAt: "updatedDate" }
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const last7 = leads.filter(l => {
    const d = new Date(l.createdDate || l.createdAt || l.created_at);
    return !isNaN(d) && d >= sevenDaysAgo;
  });
  const noLeadsThisWeek = last7.length === 0;

  // Apply status filter on 7-day leads
  let visible = last7.filter(l => filterStatus === "all" || l.status === filterStatus);

  // Apply sort
  if (sortBy === "recent") {
    visible = [...visible].sort((a, b) =>
      new Date(b.createdDate || b.createdAt || b.created_at) - new Date(a.createdDate || a.createdAt || a.created_at)
    );
  }
  if (sortBy === "interest") {
    visible = [...visible].sort((a, b) => {
      const order = { hot: 0, warm: 1, cold: 2 };
      return (order[a.interest_level] ?? 99) - (order[b.interest_level] ?? 99);
    });
  }

  // Cap visible to 6 for compact display
  visible = visible.slice(0, 6);

  return (
    <div className={`rl-card${noLeadsThisWeek ? " rl-card--alert" : ""}`}>
      {/* Header */}
      <div className="rl-header">
        <div>
          <h3>Added Leads</h3>
          <p>
            {noLeadsThisWeek
              ? <span style={{ color: "#DC2626", fontWeight: 600 }}>0 leads in the last 7 days</span>
              : <>{last7.length} leads this week · showing {visible.length}</>
            }
          </p>
        </div>
        <div className="rl-header-actions">
          <select
            className="rl-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="interested">Interested</option>
            <option value="follow_up">Follow-up</option>
          </select>
          <select
            className="rl-filter"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="recent">Most Recent</option>
            <option value="interest">By Interest</option>
          </select>
        </div>
      </div>

      {/* Cards Grid */}
      {visible.length === 0 ? (
        <div className={`rl-empty${noLeadsThisWeek ? " rl-empty--alert" : ""}`}>
          <div className={`rl-empty-icon${noLeadsThisWeek ? " rl-empty-icon--alert" : ""}`}>
            <Plus size={28} />
          </div>
          {noLeadsThisWeek ? (
            <>
              <h4 style={{ color: "#DC2626" }}>No leads in the last 7 days</h4>
              <p>No new leads have been added this week. Time to follow up!</p>
            </>
          ) : (
            <>
              <h4>No leads match the filter</h4>
              <p>Try a different status filter or add new leads</p>
            </>
          )}
          {onAddLead && (
            <button className="rl-empty-cta" onClick={onAddLead}>
              <Plus size={14} /> Add Lead
            </button>
          )}
        </div>
      ) : (
        <div className="rl-grid">
          {visible.map((lead, i) => {
            const sc        = STATUS_COLOR[lead.status] || STATUS_COLOR.new;
            const interest  = INTEREST_CONFIG[lead.interest_level];
            const initials  = (lead.full_name || "?")
              .split(" ")
              .map(n => n[0])
              .slice(0, 2)
              .join("")
              .toUpperCase();

            return (
              <motion.div
                key={lead._id}
                className="rl-item"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                {/* Top row: Avatar + name + status */}
                <div className="rl-item-top">
                  <div className="rl-avatar">{initials}</div>
                  <div className="rl-item-info">
                    <h4 title={lead.full_name}>{lead.full_name || "Unnamed"}</h4>
                    {interest && (
                      <span className="rl-interest" style={{ color: interest.color }}>
                        <interest.icon size={11} />
                        {interest.label}
                      </span>
                    )}
                  </div>
                  <span
                    className="rl-status-badge"
                    style={{ background: sc.bg, color: sc.text }}
                  >
                    {STATUS_LABEL[lead.status] || lead.status || "New"}
                  </span>
                </div>

                {/* Contact info */}
                <div className="rl-contact">
                  <a href={`tel:${lead.phone}`} className="rl-contact-item">
                    <Phone size={12} />
                    <span>{formatPhone(lead.phone)}</span>
                  </a>
                  {lead.email && (
                    <a href={`mailto:${lead.email}`} className="rl-contact-item" title={lead.email}>
                      <Mail size={12} />
                      <span>{lead.email}</span>
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="rl-footer">
        <Link to="/leads" className="rl-view-all">
          View all leads <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
};

export default RecentLeads;
